import React, { useState, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import moment from 'moment';
import { startCase, capitalize } from 'lodash';
import { themes, utils } from 'kb-shared';
import { PatientProfileResponseData } from 'kb-shared/graphql/types';
import { PATIENT } from 'kb-shared/graphql/queries';
import { useMutation } from 'react-apollo';
import gql from 'graphql-tag';

import { FieldsRow, Icon, Text, InputRow, InputsLabel, SmallWrapper, Row } from './shared';
import TextInput from 'components/TextInput/NewTextInput';
import SelectInput from 'components/SelectInput';
import { gender as pronounIcon, birthday as birthdayIcon, name, photo, sex } from 'assets/icons';
import ProfileImageUploadButton from 'components/PhotoUpload/ProfilePhoto/ProfileImageUploadButton';
import { showErrorToast } from 'utilities/notificationUtils';
import { patientQuery } from 'components/Dashboard/ProfileWidget';

const { PRONOUN_INPUT_DEFAULTS, isDateValid } = utils;

export const UPDATE_PATIENT_PROFILE = gql`
  mutation updatePatient(
    $firstName: String
    $lastName: String
    $birthday: String
    $pronoun: String
  ) {
    updatePatient(
      firstName: $firstName
      lastName: $lastName
      birthday: $birthday
      pronoun: $pronoun
    ) {
      patient {
        id
        firstName
        lastName
        birthday
        pronoun
      }
    }
  }
`;

type Props = {
  firstName?: string;
  lastName?: string;
  pronoun?: string;
  gender?: string;
  birthday?: string;
};

interface StringMap {
  [key: string]: string | undefined;
}

const validate = (nameFields: StringMap) => {
  const missingFields: string[] = [];
  Object.keys(nameFields).forEach(field => {
    if (!nameFields[field]) {
      missingFields.push(startCase(field));
    }
  });

  return missingFields;
};

export default function PatientFields({ firstName, lastName, pronoun, birthday, gender }: Props) {
  const dispatch = useDispatch();
  const [profileDetails, setProfileDetails] = useState({
    firstName,
    lastName,
    pronoun,
    birthday
  });

  const [updateProfile] = useMutation<PatientProfileResponseData>(UPDATE_PATIENT_PROFILE, {
    refetchQueries: [{ query: patientQuery }, { query: PATIENT }]
  });
  const pronounInput = PRONOUN_INPUT_DEFAULTS.find(opt => opt.value === profileDetails.pronoun);
  const handlePatientDataChange = (e: ChangeEvent<HTMLInputElement>) => {
    setProfileDetails({
      ...profileDetails,
      [e.target.name]: e.target.value
    });
  };

  const updatePatientBday = () => {
    if (!profileDetails.birthday) {
      showErrorToast("Birthdate can't be blank");
    } else if (!isDateValid(profileDetails.birthday)) {
      showErrorToast('Please input a valid date');
    } else {
      updateProfile({
        variables: {
          ...profileDetails,
          birthday: moment.tz(profileDetails.birthday, 'UTC')
        }
      }).catch(res => {
        // @ts-ignore
        const errors = res.graphQLErrors.map(error => {
          return JSON.parse(error.message);
        });
        const errorCopy = errors[0].join(', ');
        return showErrorToast(errorCopy);
      });
    }
  };
  const updatePatientName = () => {
    const errors = validate({
      firstName: profileDetails.firstName,
      lastName: profileDetails.lastName
    });
    if (errors.length > 0) {
      const errorCopy =
        errors.length > 1
          ? `${errors.join(' and ')} can't be blank`
          : `${errors[0]} can't be blank`;
      showErrorToast(capitalize(errorCopy));
    } else {
      updateProfile({
        variables: {
          ...profileDetails,
          birthday: moment.tz(profileDetails.birthday, 'UTC')
        }
      })
        .then(() => {
          dispatch({
            type: 'kindbody/user/UPDATE_NAME',
            firstName: profileDetails.firstName,
            lastName: profileDetails.lastName
          });
        })
        .catch(res => {
          // @ts-ignore
          const errors = res.graphQLErrors.map(error => {
            return JSON.parse(error.message);
          });
          const errorCopy = errors[0].join(', ');
          return showErrorToast(errorCopy);
        });
    }
  };

  return (
    <>
      <InputsLabel>
        <Icon src={photo} />
        <Text>My Photo</Text>
      </InputsLabel>
      <ImageRow>
        <ProfileImageUploadButton size={59} mode="placeholder" />
        <ImageText>CLICK TO UPLOAD OR REPLACE</ImageText>
      </ImageRow>
      <FieldsRow>
        <InputsLabel>
          <Icon src={name} />
          <Text>Legal name</Text>
        </InputsLabel>
        <InputRow>
          <Field>
            <TextInput
              name="firstName"
              label="First Name"
              value={profileDetails.firstName}
              onChange={handlePatientDataChange}
              onBlur={updatePatientName}
            />
          </Field>
          <Field>
            <TextInput
              name="lastName"
              label="Last Name"
              value={profileDetails.lastName}
              onChange={handlePatientDataChange}
              onBlur={updatePatientName}
            />
          </Field>
        </InputRow>
      </FieldsRow>
      <Row>
        <HalfRow>
          <SmallWrapper>
            <InputsLabel>
              <Icon src={pronounIcon} />
              <Text>Pronoun</Text>
            </InputsLabel>
            <SelectInput
              label="SELECT ONE"
              type={'select'}
              selectedOption={pronounInput}
              options={PRONOUN_INPUT_DEFAULTS}
              // @ts-ignore
              extractTitle={item => (item ? item.label : '')}
              onSelect={option => {
                setProfileDetails({ ...profileDetails, pronoun: option.value });
                updateProfile({
                  variables: {
                    ...profileDetails,
                    pronoun: option.value,
                    birthday: moment.tz(profileDetails.birthday, 'UTC')
                  }
                }).catch(res => {
                  // @ts-ignore
                  const errors = res.graphQLErrors.map(error => {
                    return JSON.parse(error.message);
                  });
                  const errorCopy = errors[0].join(', ');
                  return showErrorToast(errorCopy);
                });
              }}
            />
          </SmallWrapper>
          <GenderWrapper>
            <InputsLabel>
              <SexIcon src={sex} />
              <Text>Sex at Birth</Text>
            </InputsLabel>
            <GenderDetail>{gender}</GenderDetail>
          </GenderWrapper>
        </HalfRow>
        <HalfRow>
          <SmallWrapper>
            <InputsLabel>
              <Icon src={birthdayIcon} />
              <Text>Birthdate</Text>
            </InputsLabel>
            <TextInput
              name="birthday"
              label="MM/DD/YYYY"
              inputType="date"
              autoComplete="bday"
              className="birthday-input"
              value={profileDetails.birthday}
              onChange={handlePatientDataChange}
              onBlur={updatePatientBday}
            />
          </SmallWrapper>
        </HalfRow>
      </Row>
    </>
  );
}

const GenderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items; center;
  height: 94px;
  width: 50%;
  margin-right: 20px;
`;

const GenderDetail = styled.div`
  ${({ theme }) => theme.fonts.DomaineDisplay};
  color: ${themes.colors.KBNight};
  line-height: 40px;
  font-size: 24px;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid ${themes.colors.KBNight};
`;

const SexIcon = styled(Icon)`
  object-fit: cover;
  object-position: top;
`;

const ImageRow = styled.div`
  display: flex;
  flex-direction row;
  justify-content: center;
  align-items: center;
`;

const ImageText = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  color: ${themes.colors.KBNight};
  font-size: 14px;
  width: 130px;
  margin-left: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction column;
  justify-content: center;
  align-items: center;

  label {
    text-transform: uppercase;
  }
`;

const HalfRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: unset;
  align-items: start;
  width: 48%;

  ${SmallWrapper} {
    margin-right: 20px;
  }

  @media screen and (max-width: ${themes.breakpoints.tabletPortrait}) {
    width: 100%;
    margin-top: 20px;
  }
`;
